import { AppSidebar } from "@/components/AppSidebar";
import { KeyboardShortcutsHelp } from "@/components/KeyboardShortcutsHelp";
import { ReportProblemDialog } from "@/components/ReportProblemDialog";
import { TopBar } from "@/components/TopBar";
import { TabBar } from "@/components/workspace/tab-bar";
import { WorkspaceContextProvider } from "@/components/workspace/workspace-context";
import {
	OnboardingHighlight,
	OnboardingProvider,
	OnboardingStepper,
} from "@/features/onboarding";
import { QuickSwitcher } from "@/components/QuickSwitcher";
import { SplitView } from "@/components/SplitView";
import { useSplitViewStore } from "@/lib/split-view";
import { useZenStore } from "@/lib/zen-mode";

/** i18n keys for path segments used in tab titles. */
const SEGMENT_LABEL_KEYS: Record<string, string> = {
	quotes: "nav:quotes",
	orders: "nav:orders",
	products: "nav:products",
	categories: "nav:categories",
	parties: "nav:contacts",
	journal: "accounting:nav_journal",
	accounts: "accounting:nav_accounts",
	bank: "accounting:nav_bank",
	portals: "nav:portals",
	payments: "nav:payments",
	delivery: "nav:delivery",
	subscriptions: "nav:subscriptions",
	settings: "nav:settings",
	diagram: "settings:sidebar_diagram",
	activity: "nav:activity",
	diagnostics: "nav:diagnostics",
	assistant: "nav:assistant",
	fulfillment: "nav:fulfillment",
};
import { useAgentStore } from "@/lib/agent";
import { useBreadcrumbStore } from "@/lib/breadcrumbs";
import * as keyboard from "@/lib/keyboard";
import { generateBreadcrumbs } from "@/lib/route-breadcrumbs";
import { useAuthStore } from "@/lib/auth";
import {
	type EntityRef,
	getEntityDropData,
	hasEntityDrag,
} from "@/lib/entity-context";
import { useSidebarStore } from "@/lib/sidebar";
import { useTabStore } from "@/lib/tabs";
import { useRealtime } from "@/lib/use-realtime";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import { getModuleDefs } from "@/lib/workspaces";
import type { QueryClient } from "@tanstack/react-query";
import {
	Outlet,
	createRootRouteWithContext,
	useLocation,
	useRouter,
} from "@tanstack/react-router";
import { Bot } from "lucide-react";
import {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";

const AgentPanel = lazy(() =>
	import("@/components/AgentPanel").then((m) => ({ default: m.AgentPanel })),
);
import { useTranslation } from "react-i18next";

interface RouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: RootLayout,
});

const PUBLIC = ["/login", "/auth/callback", "/invite", "/widget"];

// Workspace navigation is defined in @/lib/workspaces.ts

function RootLayout() {
	const { t } = useTranslation("nav");
	const { t: tc } = useTranslation("common");
	const { isAuthenticated, user } = useAuthStore();
	const [hydrated, setHydrated] = useState(false);
	useEffect(() => {
		// Wait for zustand persist to rehydrate from
		// localStorage before making auth decisions
		const unsub = useAuthStore.persist.onFinishHydration(() => {
			setHydrated(true);
		});
		if (useAuthStore.persist.hasHydrated()) {
			setHydrated(true);
		}
		return unsub;
	}, []);
	const location = useLocation();
	const router = useRouter();
	const { setOpen } = useSidebarStore();
	const { setPage } = useRealtime();
	const selectedWorkspace = useWorkspaceStore((s) => s.selected());
	const wsFeatures = selectedWorkspace?.features ?? [];
	const agentOpen = useAgentStore((s) => s.isOpen);
	const agentSetOpen = useAgentStore((s) => s.setOpen);
	const agentToggle = useAgentStore((s) => s.toggle);
	const isZen = useZenStore((s) => s.isZen);
	const zenToggle = useZenStore((s) => s.toggle);
	const splitEnable = useSplitViewStore((s) => s.enableSplit);

	const moduleDefs = useMemo(() => getModuleDefs(), []);

	// ── Global tabs ──
	const tabStore = useTabStore();
	const globalTabs = tabStore.tabs;
	const activeTabId = tabStore.activeTabId;

	// Derive a title for the current route from module defs or page path
	const routeTitle = useMemo(() => {
		const path = location.pathname;
		for (const mod of moduleDefs) {
			if (path.startsWith(mod.prefix)) {
				// Exact match on a nav item
				const exactItem = mod.items.find((i) => path === i.to);
				if (exactItem) return t(exactItem.labelKey);
				// Prefix match — find the deepest matching item
				const prefixItem = mod.items
					.filter((i) => path.startsWith(i.to))
					.sort((a, b) => b.to.length - a.to.length)[0];
				if (prefixItem) return t(prefixItem.labelKey);
				return t(mod.labelKey);
			}
		}
		if (path === "/") return t("home");
		if (path.startsWith("/settings")) {
			const segments = path.split("/").filter(Boolean);
			const last = segments[segments.length - 1];
			const key = SEGMENT_LABEL_KEYS[last];
			return key ? t(key) : t("nav:settings");
		}
		// Fallback: last segment, but clean up UUIDs
		const last = path.split("/").filter(Boolean).pop() ?? "";
		if (/^[0-9a-f-]{20,}$/.test(last)) {
			const segments = path.split("/").filter(Boolean);
			const parent = segments[segments.length - 2];
			const key = SEGMENT_LABEL_KEYS[parent];
			return key ? t(key) : (parent ?? "");
		}
		const key = SEGMENT_LABEL_KEYS[last];
		return key ? t(key) : last;
	}, [location.pathname, moduleDefs, t]);

	// Sync current route with tab store + auto-generate breadcrumbs
	useEffect(() => {
		if (!isAuthenticated) return;
		const path = location.pathname;
		// Don't create tabs for login/auth/widget/admin
		if (PUBLIC.some((p) => path.startsWith(p)) || path.startsWith("/admin"))
			return;
		// Only create tabs for known module routes and home — skip settings,
		// diagnostics sub-pages, and other routes that produce untranslated slugs.
		const isHome = path === "/";
		const parentModule = moduleDefs.find(
			(m) => path.startsWith(m.prefix) || m.items.some((i) => path.startsWith(i.to)),
		);
		// Paths with their own Workspace layout — only one tab for the root.
		const workspaceRoots = ["/accounting", "/commerce", "/diagnostics", "/manufacturing"];
		const wsRoot = workspaceRoots.find((r) => path.startsWith(r));

		if (isHome) {
			tabStore.syncWithPath(path, routeTitle);
		} else if (wsRoot) {
			// Workspace modules get ONE tab — sub-routes navigate internally.
			const matchedItem = parentModule?.items.find((i) => path.startsWith(i.to));
			const title = matchedItem ? t(matchedItem.labelKey) : (parentModule ? t(parentModule.labelKey) : routeTitle);
			tabStore.syncWithPath(wsRoot, title);
		} else if (parentModule) {
			tabStore.syncWithPath(path, routeTitle);
		} else {
			// Still mark the tab as active if it already exists (user manually pinned it)
			const id = path.replace(/\/+$/, "") || "/";
			if (tabStore.tabs.find((t) => t.id === id)) {
				tabStore.setActiveTab(id);
			}
		}

		// Auto-generate breadcrumbs from path
		const { setAutoCrumbs } = useBreadcrumbStore.getState();
		setAutoCrumbs(generateBreadcrumbs(path, routeTitle, t));
	}, [location.pathname, isAuthenticated, routeTitle]);

	const handleTabChange = useCallback(
		(id: string) => {
			tabStore.setActiveTab(id);
			const tab = globalTabs.find((t) => t.id === id);
			if (tab) router.navigate({ to: tab.path });
		},
		[tabStore, globalTabs, router],
	);

	const handleTabClose = useCallback(
		(id: string) => {
			const nextPath = tabStore.closeTab(id);
			if (nextPath) router.navigate({ to: nextPath });
		},
		[tabStore, router],
	);
	// activeModule used by sidebar (now in AppSidebar component)

	// Pending entities queued via drag-drop when agent panel was closed
	const [pendingEntities, setPendingEntities] = useState<EntityRef[]>([]);
	const [edgeDragOver, setEdgeDragOver] = useState(false);

	const onEdgeDragOver = useCallback((e: React.DragEvent) => {
		if (
			hasEntityDrag(e) ||
			e.dataTransfer.types.includes("application/x-omnios-entities")
		) {
			e.preventDefault();
			e.dataTransfer.dropEffect = "copy";
			setEdgeDragOver(true);
		}
	}, []);
	const onEdgeDragLeave = useCallback((e: React.DragEvent) => {
		if (!e.currentTarget.contains(e.relatedTarget as Node)) {
			setEdgeDragOver(false);
		}
	}, []);
	const onEdgeDrop = useCallback(
		(e: React.DragEvent) => {
			setEdgeDragOver(false);
			e.preventDefault();
			const collected: EntityRef[] = [];
			const multiRaw = e.dataTransfer.getData("application/x-omnios-entities");
			if (multiRaw) {
				try {
					collected.push(...(JSON.parse(multiRaw) as EntityRef[]));
				} catch {
					/* */
				}
			}
			if (collected.length === 0) {
				const entity = getEntityDropData(e);
				if (entity) collected.push(entity);
			}
			if (collected.length > 0) {
				setPendingEntities(collected);
				agentSetOpen(true);
			}
		},
		[agentSetOpen],
	);

	// Global keyboard shortcut dispatcher
	useEffect(() => {
		document.addEventListener("keydown", keyboard.handleKeyDown);
		return () =>
			document.removeEventListener("keydown", keyboard.handleKeyDown);
	}, []);

	// Register global shortcuts
	useEffect(() => {
		const unregs = [
			keyboard.registerShortcut({
				id: "global.zen-mode",
				keys: "meta+shift+z",
				label: "shortcuts_zen",
				displayKeys: "⌘⇧Z",
				category: "global",
				action: () => zenToggle(),
			}),
			keyboard.registerShortcut({
				id: "global.split-view",
				keys: "meta+\\",
				label: "shortcuts_split",
				displayKeys: "⌘\\",
				category: "global",
				action: () => {
					const path = window.location.pathname;
					splitEnable({ id: `split-${Date.now()}`, path, title: "Split" });
				},
			}),
			keyboard.registerShortcut({
				id: "global.agent-toggle",
				keys: "meta+j",
				label: "shortcuts_agent",
				displayKeys: "⌘J",
				category: "global",
				action: () => agentToggle(),
			}),
			keyboard.registerShortcut({
				id: "navigation.close-tab",
				keys: "meta+w",
				label: "shortcuts_close_tab",
				displayKeys: "⌘W",
				category: "navigation",
				action: () => {
					const { activeTabId } = useTabStore.getState();
					if (activeTabId) {
						const nextPath = tabStore.closeTab(activeTabId);
						if (nextPath) router.navigate({ to: nextPath });
					}
				},
			}),
			keyboard.registerShortcut({
				id: "navigation.reopen-tab",
				keys: "meta+shift+t",
				label: "shortcuts_reopen_tab",
				displayKeys: "⌘⇧T",
				category: "navigation",
				action: () => {
					const path = tabStore.reopenTab();
					if (path) router.navigate({ to: path });
				},
			}),
			keyboard.registerShortcut({
				id: "global.new",
				keys: "meta+n",
				label: "shortcuts_new",
				displayKeys: "⌘N",
				category: "global",
				action: () => {
					const path = window.location.pathname;
					if (path.startsWith("/accounting")) {
						router.navigate({ to: "/accounting/journal/new" });
					} else if (path.startsWith("/catalog/products")) {
						router.navigate({
							to: "/catalog/products",
							search: { owner_party_id: undefined },
						});
					}
				},
			}),
			// Cmd+1..9 — quick module switching
			...Array.from({ length: 9 }, (_, i) => {
				const digit = i + 1;
				return keyboard.registerShortcut({
					id: `navigation.module-${digit}`,
					keys: `meta+${digit}`,
					label: "shortcuts_workspace",
					displayKeys: `⌘${digit}`,
					category: "navigation",
					action: () => {
						const defs = moduleDefs.filter(
							(m) => !m.feature || wsFeatures.includes(m.feature),
						);
						const mod = defs[i];
						if (mod) {
							router.navigate({ to: mod.items[0]?.to ?? mod.prefix });
						}
					},
				});
			}),
		];
		return () => {
			for (const u of unregs) u();
		};
	}, [agentToggle, zenToggle, splitEnable, router, moduleDefs, wsFeatures]);

	const isPublic = PUBLIC.some((p) => location.pathname.startsWith(p));

	useEffect(() => {
		if (hydrated && !isAuthenticated && !isPublic) {
			router.navigate({ to: "/login" });
		}
	}, [hydrated, isAuthenticated, isPublic, router, location.pathname]);

	useEffect(() => {
		// Close sidebar on mobile navigation only
		if (window.innerWidth < 1024) {
			setOpen(false);
		}
		if (isAuthenticated) {
			setPage(location.pathname);
		}
	}, [location.pathname, setOpen, isAuthenticated, setPage]);

	const isAdmin = location.pathname.startsWith("/admin");
	const isAssistantPage = location.pathname.startsWith("/assistant");

	if (!hydrated) return null;
	if (!isAuthenticated && !isPublic) return null;
	if (isPublic || isAdmin) return <Outlet />;

	return (
		<OnboardingProvider>
			<WorkspaceContextProvider>
				<div className="flex h-screen overflow-hidden">
					{!isZen && <AppSidebar isSuperAdmin={user?.isSuperAdmin} />}

					<div className="flex flex-1 overflow-hidden">
						<div className="flex flex-1 flex-col overflow-hidden min-w-0">
							{!isZen && <TopBar />}
							<OnboardingStepper />
							<OnboardingHighlight />
							{(() => {
								const isFullBleed =
									location.pathname.startsWith("/accounting") ||
									location.pathname.startsWith("/commerce") ||
									location.pathname.startsWith("/diagnostics") ||
									location.pathname.startsWith("/manufacturing") ||
									location.pathname.startsWith("/background-jobs") ||
									location.pathname.startsWith("/settings") ||
									location.pathname.startsWith("/activity") ||
									location.pathname.startsWith("/catalog") ||
									location.pathname.startsWith("/work-items") ||
									location.pathname.startsWith("/parties") ||
									location.pathname.startsWith("/portals") ||
									location.pathname.startsWith("/assistant") ||
									location.pathname.startsWith("/docs");
								return isFullBleed ? (
									<SplitView>
										<main className="flex-1 overflow-hidden">
											<Outlet />
										</main>
									</SplitView>
								) : (
									<SplitView>
										<main className="flex-1 overflow-y-auto p-6">
											<Outlet />
										</main>
									</SplitView>
								);
							})()}
							{!isZen && (
								<TabBar
									tabs={globalTabs}
									activeTabId={activeTabId ?? ""}
									onTabChange={handleTabChange}
									onTabClose={handleTabClose}
									onCloseOthers={(id) => tabStore.closeOthers(id)}
									onCloseRight={(id) => tabStore.closeRight(id)}
									onReorder={(from, to) => tabStore.reorderTabs(from, to)}
									onTogglePin={(id) => tabStore.togglePin(id)}
								/>
							)}
						</div>
						{!isAssistantPage && agentOpen ? (
							<Suspense fallback={null}>
								<AgentPanel
									pendingEntities={pendingEntities}
									onPendingConsumed={() => setPendingEntities([])}
								/>
							</Suspense>
						) : !isAssistantPage ? (
							<div
								className={cn(
									"flex shrink-0 flex-col items-center justify-center gap-2 transition-all",
									edgeDragOver
										? "w-48 border-l border-primary border-dashed bg-primary/10"
										: "w-1",
								)}
								onDragOver={onEdgeDragOver}
								onDragLeave={onEdgeDragLeave}
								onDrop={onEdgeDrop}
							>
								{edgeDragOver && (
									<>
										<Bot className="h-6 w-6 text-primary animate-pulse" />
										<span className="text-xs text-center px-3 text-primary font-medium">
											{tc("drop_to_open")}
										</span>
									</>
								)}
							</div>
						) : null}
					</div>
				</div>
			</WorkspaceContextProvider>
			<ReportProblemDialog />
			<KeyboardShortcutsHelp />
			<QuickSwitcher />
		</OnboardingProvider>
	);
}
